const FUNCTIONS = {
  sin: (value, angleMode) => Math.sin(toRadians(value, angleMode)),
  cos: (value, angleMode) => Math.cos(toRadians(value, angleMode)),
  tan: (value, angleMode) => Math.tan(toRadians(value, angleMode)),
  asin: (value, angleMode) => fromRadians(Math.asin(value), angleMode),
  acos: (value, angleMode) => fromRadians(Math.acos(value), angleMode),
  atan: (value, angleMode) => fromRadians(Math.atan(value), angleMode),
  ln: value => Math.log(value),
  log: value => Math.log(value) / Math.LN10,
  sqrt: value => Math.sqrt(value),
  abs: value => Math.abs(value),
  exp: value => Math.exp(value)
};

function toRadians(value, angleMode) {
  return angleMode === "DEG" ? value * Math.PI / 180 : value;
}

function fromRadians(value, angleMode) {
  return angleMode === "DEG" ? value * 180 / Math.PI : value;
}

function factorial(value) {
  if (!Number.isInteger(value) || value < 0 || value > 170) {
    throw new Error("Factorial requires an integer from 0 to 170");
  }
  let result = 1;
  for (let current = 2; current <= value; current += 1) {
    result *= current;
  }
  return result;
}

function tokenize(expression) {
  const tokens = [];
  let index = 0;

  while (index < expression.length) {
    const rest = expression.slice(index);
    const character = expression[index];

    if (/\s/.test(character)) {
      index += 1;
      continue;
    }

    const numberMatch = rest.match(/^(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?/);
    if (numberMatch) {
      tokens.push({ type: "number", value: Number(numberMatch[0]) });
      index += numberMatch[0].length;
      continue;
    }

    const identifierMatch = rest.match(/^[a-z]+/i);
    if (identifierMatch) {
      tokens.push({ type: "identifier", value: identifierMatch[0].toLowerCase() });
      index += identifierMatch[0].length;
      continue;
    }

    if (character === "π") {
      tokens.push({ type: "identifier", value: "pi" });
      index += 1;
      continue;
    }

    if ("+-*/^()%!".indexOf(character) !== -1) {
      tokens.push({ type: "operator", value: character });
      index += 1;
      continue;
    }

    throw new Error("Unsupported character");
  }

  return tokens;
}

export function evaluateScientificExpression(expression, options = {}) {
  const angleMode = options.angleMode === "RAD" ? "RAD" : "DEG";
  const ans = Number.isFinite(options.ans) ? options.ans : 0;
  const tokens = tokenize(expression);
  let position = 0;

  function peek(value) {
    const token = tokens[position];
    return token && (typeof value === "undefined" || token.value === value);
  }

  function take(value) {
    if (!peek(value)) {
      throw new Error("Incomplete expression");
    }
    return tokens[position++];
  }

  function parsePrimary() {
    const token = tokens[position];
    if (!token) throw new Error("Incomplete expression");

    if (token.type === "number") {
      position += 1;
      return token.value;
    }

    if (token.value === "(") {
      position += 1;
      const value = parseAdditive();
      take(")");
      return value;
    }

    if (token.type === "identifier") {
      position += 1;
      if (token.value === "pi") return Math.PI;
      if (token.value === "e") return Math.E;
      if (token.value === "ans") return ans;
      if (!FUNCTIONS[token.value]) throw new Error("Unknown function");
      take("(");
      const argument = parseAdditive();
      take(")");
      return FUNCTIONS[token.value](argument, angleMode);
    }

    throw new Error("Expected a number");
  }

  function parsePostfix() {
    let value = parsePrimary();
    while (peek("!") || peek("%")) {
      if (take().value === "!") value = factorial(value);
      else value /= 100;
    }
    return value;
  }

  function parsePower() {
    const value = parsePostfix();
    if (peek("^")) {
      take("^");
      return Math.pow(value, parseUnary());
    }
    return value;
  }

  function parseUnary() {
    if (peek("+")) {
      take("+");
      return parseUnary();
    }
    if (peek("-")) {
      take("-");
      return -parseUnary();
    }
    return parsePower();
  }

  function parseMultiplicative() {
    let value = parseUnary();
    while (peek("*") || peek("/")) {
      const operator = take().value;
      const right = parseUnary();
      value = operator === "*" ? value * right : value / right;
    }
    return value;
  }

  function parseAdditive() {
    let value = parseMultiplicative();
    while (peek("+") || peek("-")) {
      const operator = take().value;
      const right = parseMultiplicative();
      value = operator === "+" ? value + right : value - right;
    }
    return value;
  }

  if (!tokens.length) throw new Error("Enter a calculation");
  const result = parseAdditive();
  if (position !== tokens.length) throw new Error("Check the expression");
  if (!Number.isFinite(result)) throw new Error("Result is undefined");
  return result;
}

export function formatCalculatorNumber(value) {
  if (!Number.isFinite(value)) return "Error";
  if (Object.is(value, -0)) return "0";
  const magnitude = Math.abs(value);
  if (magnitude !== 0 && (magnitude >= 1e12 || magnitude < 1e-9)) {
    return value
      .toExponential(10)
      .replace(/(\.\d*?[1-9])0+e/, "$1e")
      .replace(/\.0+e/, "e");
  }
  return Number(value.toPrecision(12)).toString();
}
